import { Charity, CharityUpdate } from "@models/Charity";
import { convertIdInArray, convertIdInObject } from "@util/convert-id-field";
import createFormData from "@util/create-form.data";
import { AxiosProgressEvent } from "axios";
import apiClient from "./apiClient";

interface CharityWithId extends Omit<Charity, "id"> {
    _id: string;
}

export const fetchCharities = async (): Promise<Charity[]> => {
    try {
        const response = await apiClient.get<CharityWithId[]>("/charity");
        return convertIdInArray(response.data);
    } catch (error) {
        console.error("Failed to fetch charities", error);
        throw error;
    }
};

export const fetchCharity = async (id: string): Promise<Charity> => {
    try {
        const response = await apiClient.get<CharityWithId>(`/charity/${id}`);
        return convertIdInObject(response.data);
    } catch (error) {
        console.error("Failed to fetch charity", error);
        throw error;
    }
};

export const createCharity = async (
    charity: CharityUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<string> => {
    try {
        const formData = createFormData(charity);

        const response = await apiClient.post("/charity", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });

        return response.data._id;
    } catch (error) {
        console.error("Failed to create charity", error);
        throw error;
    }
};

export const updateCharity = async (
    id: string,
    updates: CharityUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<void> => {
    try {
        const formData = createFormData(updates);

        await apiClient.patch(`/charity/${id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });
    } catch (error) {
        console.error("Failed to update charity", error);
        throw error;
    }
};

export const deleteCharityLogo = async (id: string): Promise<void> => {
    try {
        await apiClient.delete(`/charity/delete-logo/${id}`);
    } catch (error) {
        console.error("Failed to delete charity logo", error);
        throw error;
    }
};
